import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [popupNotifications, setPopupNotifications] = useState([]);
    const [recentAssignments, setRecentAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();

    // Fetch unread count
    const fetchUnreadCount = async () => {
        if (!user || user.is_admin) return;

        try {
            const response = await api.get('/notifications/unread-count');
            setUnreadCount(response.data.count || 0);
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    };

    // Fetch all notifications
    const fetchNotifications = async (unreadOnly = false) => {
        if (!user || user.is_admin) return;

        setLoading(true);
        try {
            const response = await api.get('/notifications', {
                params: { unread_only: unreadOnly }
            });
            setNotifications(response.data.notifications || []);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch popup notifications
    const fetchPopupNotifications = async () => {
        if (!user || user.is_admin) return;

        try {
            const response = await api.get('/notifications/popup');
            setPopupNotifications(response.data.notifications || []);
        } catch (error) {
            console.error('Error fetching popup notifications:', error);
        }
    };

    // Fetch recent task assignments
    const fetchRecentAssignments = async (limit = 5) => {
        if (!user || user.is_admin) return;

        try {
            const response = await api.get('/notifications/recent-assignments', {
                params: { limit }
            });
            setRecentAssignments(response.data.recentAssignments || []);
        } catch (error) {
            console.error('Error fetching recent assignments:', error);
        }
    };

    // Mark notification as read
    const markAsRead = async (notificationId) => {
        try {
            await api.put(`/notifications/${notificationId}/read`);

            // Update local state
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
            );
            setPopupNotifications(prev =>
                prev.filter(n => n.id !== notificationId)
            );

            // Refresh unread count
            fetchUnreadCount();
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    // Mark all notifications as read
    const markAllAsRead = async () => {
        try {
            await api.put('/notifications/read-all');

            // Update local state
            setNotifications(prev =>
                prev.map(n => ({ ...n, is_read: true }))
            );
            setPopupNotifications([]);
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    // Dismiss notification
    const dismissNotification = async (notificationId) => {
        try {
            await api.put(`/notifications/${notificationId}/dismiss`);

            // Update local state
            setNotifications(prev =>
                prev.filter(n => n.id !== notificationId)
            );
            setPopupNotifications(prev =>
                prev.filter(n => n.id !== notificationId)
            );
            setRecentAssignments(prev =>
                prev.filter(n => n.id !== notificationId)
            );

            // Refresh unread count
            fetchUnreadCount();
        } catch (error) {
            console.error('Error dismissing notification:', error);
        }
    };

    // Dismiss all notifications (clear all)
    const dismissAllNotifications = async () => {
        try {
            await api.delete('/notifications/dismiss-all');

            // Update local state
            setNotifications([]);
            setPopupNotifications([]);
            setRecentAssignments([]);
            setUnreadCount(0);
        } catch (error) {
            console.error('Error dismissing all notifications:', error);
        }
    };

    // Refresh all notification data
    const refreshNotifications = () => {
        fetchUnreadCount();
        fetchPopupNotifications();
        fetchRecentAssignments();
    };

    // Initial fetch and polling
    useEffect(() => {
        if (user && !user.is_admin) {
            fetchUnreadCount();
            fetchPopupNotifications();
            fetchRecentAssignments();

            // Poll for new notifications every 30 seconds
            const interval = setInterval(() => {
                fetchUnreadCount();
                fetchPopupNotifications();
            }, 30000);

            return () => clearInterval(interval);
        }
    }, [user]);

    const value = {
        notifications,
        unreadCount,
        popupNotifications,
        recentAssignments,
        loading,
        fetchNotifications,
        fetchUnreadCount,
        fetchPopupNotifications,
        fetchRecentAssignments,
        markAsRead,
        markAllAsRead,
        dismissNotification,
        dismissAllNotifications,
        refreshNotifications
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

